import { EcoaMediaItems } from "@components/common/EcoaMediaItems";
import { EcoaRadio } from "@components/common/EcoaRadio";
import { useCallback, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { useLangContext } from "@layouts/home/LangContext";
import { VisitQuestionnaireAnswer } from "@pages/ecoa/visit/VisitQuestionnaire";
import { CrfSingleSelectItemSpec } from "maven-lib/dist/consts/edc";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { classNames } from "maven-lib/dist/utils/misc";

interface StyledRadioItemsProps {
  isHorizontal: boolean;
  iconLength: number;
}

const StyledRadioItems = styled.div<StyledRadioItemsProps>`
  ${({ isHorizontal, iconLength }) =>
    isHorizontal &&
    !!iconLength &&
    `
    &.--direction-horizontal {
      .hb-radio-items {
        flex-wrap: wrap;
        justify-content: center;

        .hb-radio-item {
          flex-basis: calc((100% - 0.8rem * ${iconLength})/${iconLength});
          min-width: 55px;
          
          &.--icon {
            justify-content: normal;

            .hb-radio-item-icon {
              width: auto;
            }
          } 
        }
      }
    }
  `}
`;

interface QuestionnaireRadioItemProps {
  questionnaire: EcoaQuestionnaire;
  isAvailable?: boolean;
  item: CrfSingleSelectItemSpec;
  disabled?: boolean;
  className?: string;
  defaultItemValues?: Array<VisitQuestionnaireAnswer>;
  onValueChange: (values: Array<VisitQuestionnaireAnswer>) => void;
}

export function QuestionnaireRadioItem({
  className,
  disabled,
  defaultItemValues,
  isAvailable = true,
  item,
  onValueChange,
}: QuestionnaireRadioItemProps) {
  const { fontSize } = useLangContext();
  const { codes } = item.itemCode;
  const itemDbValues = useMemo(
    () => defaultItemValues?.map((e) => ({ itemId: e.itemId, dbVal: e.dbVal, calcVal: e.calcVal })) || new Array(codes.length),
    [codes.length, defaultItemValues]
  );
  const selectedRadioItemDbVal = (
    itemDbValues.find((itemValue) => itemValue.itemId === item.id) || { itemId: null, dbVal: null, calcVal: null }
  ).dbVal;

  const handleItemDbValuesChange = useCallback(
    (data: Array<VisitQuestionnaireAnswer>) => {
      onValueChange(data);
    },
    [onValueChange]
  );

  useEffect(() => {
    if (
      !isAvailable &&
      defaultItemValues?.length &&
      itemDbValues.find((itemValue) => itemValue.itemId === item.id)?.dbVal !== null &&
      !item.ecoa?.noSkipWhenDisabled
    ) {
      const typeTextCodes = codes.filter((code) => code.typeText).map((code) => code.typeText?.id) || [];
      handleItemDbValuesChange(
        defaultItemValues.map((defaultItem) =>
          defaultItem.itemId === item.id || typeTextCodes.includes(defaultItem.itemId || "")
            ? { ...defaultItem, dbVal: null, calcVal: undefined }
            : defaultItem
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes, defaultItemValues, isAvailable, item.id, itemDbValues, handleItemDbValuesChange]);

  return (
    <>
      <StyledRadioItems
        className={classNames(
          "hb-radio",
          className,
          disabled && "--disabled",
          `--direction-${String(item.direction || "VERTICAL").toLowerCase()}`,
          `--appearance-${String(item.direction === "HORIZONTAL" || codes.find((code) => code.icon) ? "BORDERED" : "BULLET").toLowerCase()}`
        )}
        isHorizontal={item.direction === "HORIZONTAL"}
        iconLength={codes.some((code) => code.icon) ? codes.length : 0}
      >
        <div className="hb-radio-items">
          {codes.map((code) => (
            <EcoaRadio
              key={code.dbVal}
              disabled={disabled}
              onValueChange={handleItemDbValuesChange}
              values={itemDbValues}
              fontSize={fontSize}
              selectedValue={selectedRadioItemDbVal}
              direction={item.direction}
              item={{
                id: item.id,
                key: code.dbVal,
                icon: code.icon,
                img: !!code.uiImg && "url" in code.uiImg ? { url: code.uiImg.url } : undefined,
                slotPosition: code.uiMedia?.display?.position || code.uiImg?.position,
                slot: (
                  <EcoaMediaItems
                    media={code.uiMedia?.media}
                    placement={code.uiMedia?.display?.placement}
                    alignment={code.uiMedia?.display?.alignment}
                  />
                ),
                text: code.uiVal || "-",
                typeText: code.typeText,
                calcVal: code.calcVal,
                isSameFontSizeForQNA: item.ecoa?.isSameFontSizeForQNA,
              }}
            />
          ))}
        </div>
      </StyledRadioItems>
    </>
  );
}
